//== display formatting and input parsing helpers (en-IE)
//== shared by every calculator - product-agnostic, no DOM dependencies.
//== moved here from redemption-calculator/ on 2026-08-18 when the service
//== charges calculator became a second consumer (new-calculators.md §7).
//== money: € prefix, en-IE thousands separators
//== percent: percentage form (e.g. 22.5), suffixed with %
//== state stores plain Number; these helpers cross the display boundary.

const LOCALE = 'en-IE'
const CURRENCY_SYMBOL = '\u20AC' //== euro sign, escaped to keep this file pure ASCII so umbraco / .net pipelines cannot transcode it

export function parseMoney(value) {
  if (value == null) return null
  const cleaned = String(value).replace(/[^\d.\-]/g, '')
  if (!cleaned || cleaned === '-' || cleaned === '.') return null
  const num = Number(cleaned)
  return Number.isFinite(num) ? num : null
}

//== round to 2dp, then show 0 or 2 decimals - never 1 or 3+. so calc
//== output €45,833.333 -> €45,833.33, whole values stay clean (€45,000),
//== and €45,000.50 keeps its trailing zero rather than collapsing to .5.
export function formatMoney(num) {
  if (!Number.isFinite(num)) return ''
  const rounded = Math.round(num * 100) / 100
  const fractionDigits = Number.isInteger(rounded) ? 0 : 2
  return `${CURRENCY_SYMBOL}${rounded.toLocaleString(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

//== money at a fixed number of decimals - €0 -> €0.00, €1068.75 -> €1,068.75.
//== added 2026-08-18 for the service charges table, where a column of figures
//== needs consistent decimals; formatMoney's integer collapse would print the
//== zero-rate band as €0 next to €54.69. matches the workbook's 2dp number
//== format on those cells. formatMoney is unchanged.
export function formatMoneyFixed(num, fractionDigits = 2) {
  if (!Number.isFinite(num)) return ''
  return `${CURRENCY_SYMBOL}${num.toLocaleString(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

//== compact thousands form for the summary panel - €40,000 -> €40k,
//== €30,750 -> €30.75k, €237,500 -> €237.5k. up to 2dp; trailing zeros
//== stripped via parseFloat so round thousands stay clean. values under
//== €1,000 fall back to the full format.
export function formatMoneyCompact(num) {
  if (!Number.isFinite(num)) return ''
  if (Math.abs(num) < 1000) return formatMoney(num)
  const display = String(parseFloat((num / 1000).toFixed(2)))
  return `${CURRENCY_SYMBOL}${display}k`
}

export function parsePercent(value) {
  if (value == null) return null
  const cleaned = String(value).replace(/[^\d.\-]/g, '')
  if (!cleaned || cleaned === '-' || cleaned === '.') return null
  const num = Number(cleaned)
  return Number.isFinite(num) ? num : null
}

export function formatPercent(num, fractionDigits = 2) {
  if (!Number.isFinite(num)) return ''
  return `${num.toFixed(fractionDigits)}%`
}

//== ISO date string -> DD/MM/YYYY, parsed manually so we don't pick up
//== timezone shifts from new Date('2026-04-30') being treated as UTC.
export function formatDate(value) {
  if (!value) return ''
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return ''
  const [, y, m, d] = match
  return `${d}/${m}/${y}`
}
